import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import {
    createAdminApp,
    normalizeApiErrorDetails,
    type ProvisionedRegistryApp,
} from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { useToast } from '@/components/ui';

// EAA-1817: the design spec's mã pattern — lowercase, digits, hyphens only.
// Stricter than the server's SlugField (which also accepts `_` and mixed
// case), so a code that validates here is always accepted there too.
const CODE_PATTERN = /^[a-z0-9-]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// EAA-1845: `labelKey` (an i18n key), not the display text itself — this array
// is module scope, outside React, so it cannot call `useTranslation()`. The
// caller (`AppsCreatePage`, formerly `CreateAppModal`) resolves the key at
// render time via `t(option.labelKey)`.
export const APP_TYPE_OPTIONS = [
    { value: 'web', labelKey: 'console.apps.createForm.appTypeWeb' },
    { value: 'api', labelKey: 'console.apps.createForm.appTypeApi' },
    { value: 'hybrid', labelKey: 'console.apps.createForm.appTypeHybrid' },
] as const;
export type AppTypeValue = (typeof APP_TYPE_OPTIONS)[number]['value'];

type OrgScopeMode = 'selected_orgs' | 'all_orgs';

interface ScalarFields {
    code: string;
    name: string;
    description: string;
    icon_url: string;
    app_type: AppTypeValue;
    owner_name: string;
    owner_email: string;
    web_url: string;
    api_base: string;
    audience: string;
    org_scope_mode: OrgScopeMode;
    // EAA-1841: opt-in claim that this app implements the role-management
    // receiver (`POST /api/v1/management/roles`). Default false — an app that
    // has not implemented it must never be offered the create-role action,
    // because the call would be refused rather than quietly doing nothing.
    supports_role_management: boolean;
}

const EMPTY_SCALARS: ScalarFields = {
    code: '',
    name: '',
    description: '',
    icon_url: '',
    app_type: 'web',
    owner_name: '',
    owner_email: '',
    web_url: '',
    api_base: '',
    audience: '',
    org_scope_mode: 'selected_orgs',
    supports_role_management: false,
};

/**
 * A redirect URI is valid empty (dropped before submit) or http(s) and not a
 * bare wildcard — mirrors portal-api's redirect-URI validator (rejects both a
 * non-http(s) scheme and a lone `*`), per this ticket's brief (EAA-1817,
 * pairs with portal-api EAA-1840).
 */
function isValidUri(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) return true;
    if (trimmed === '*') return false;
    return /^https?:\/\//.test(trimmed);
}

function uriListError(
    values: string[],
    required: boolean,
    t: (key: string) => string
): string | null {
    const nonEmpty = values.map((v) => v.trim()).filter(Boolean);
    if (required && nonEmpty.length === 0) {
        return t('console.apps.createForm.uriRequiredError');
    }
    if (values.some((v) => !isValidUri(v))) {
        return t('console.apps.createForm.uriInvalidError');
    }
    return null;
}

/**
 * EAA-1817: form state + submit mutation for the "Thêm ứng dụng" form, split
 * out so the consuming component stays under the page/component size ceiling
 * with this many fields. Originally consumed by the `CreateAppModal` dialog;
 * EAA-1846 moved that flow to the routed `AppsCreatePage` (the modal is
 * deleted — this hook needed no logic change, only a different caller and an
 * `onCreated` that navigates instead of closing a dialog). See git history on
 * `CreateAppModal.tsx` for the field-by-field backend verification note this
 * replaces (the five fields it once called an unsupported gap —
 * app_type/owner_name/owner_email/redirect_uris/org_scope_mode — now have
 * a real place to land per the EAA-1817/EAA-1840 pairing).
 *
 * `createAdminApp`'s parameter type (`AdminAppWritableFields` in `@/api/console`)
 * names every field sent below. EAA-1709: `onCreated` receives the created row,
 * including its `provisioning` block, so the caller can reveal a one-time
 * machine-client secret before navigating away.
 */
export function useCreateAppForm(onCreated: (created: ProvisionedRegistryApp) => void) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const [scalars, setScalars] = useState<ScalarFields>(EMPTY_SCALARS);
    const [redirectUris, setRedirectUris] = useState<string[]>(['']);
    const [postLogoutUris, setPostLogoutUris] = useState<string[]>(['']);
    const [codeTouched, setCodeTouched] = useState(false);
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
    const [formError, setFormError] = useState<string | null>(null);

    function setScalar<K extends keyof ScalarFields>(key: K, value: ScalarFields[K]) {
        setScalars((prev) => ({ ...prev, [key]: value }));
        setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: [] } : prev));
    }

    function markTouched(key: string) {
        setTouched((prev) => ({ ...prev, [key]: true }));
    }

    const trimmedCode = scalars.code.trim();
    const trimmedName = scalars.name.trim();
    const trimmedWebUrl = scalars.web_url.trim();
    const trimmedIconUrl = scalars.icon_url.trim();
    const trimmedOwnerEmail = scalars.owner_email.trim();

    const codeValid = CODE_PATTERN.test(trimmedCode);
    // EAA-1817: web URL is optional, but if given it must be https — api_base
    // stays looser since dev/staging environments legitimately run it over
    // http (server's own validator accepts either scheme for that field).
    const webUrlValid = trimmedWebUrl === '' || trimmedWebUrl.startsWith('https://');
    // WHY: icon_url had no client-side check at all, so a non-URL value (e.g.
    // "a") only ever surfaced as a raw backend 400 ("Enter a valid http(s)
    // URL.") after a round-trip. Mirrors web_url's shape but allows either
    // scheme, matching the server's own (looser, http-or-https) validator for
    // this field — an icon asset legitimately being served over http in a
    // dev/staging environment isn't the same risk as a login redirect target.
    const iconUrlValid = trimmedIconUrl === '' || /^https?:\/\//.test(trimmedIconUrl);
    const ownerEmailValid = trimmedOwnerEmail === '' || EMAIL_PATTERN.test(trimmedOwnerEmail);
    const redirectError = uriListError(redirectUris, true, t);
    const postLogoutError = uriListError(postLogoutUris, false, t);

    const canSubmit =
        codeValid &&
        trimmedName.length > 0 &&
        webUrlValid &&
        iconUrlValid &&
        ownerEmailValid &&
        !redirectError &&
        !postLogoutError;

    const createMutation = useMutation({
        mutationFn: () => {
            const payload = {
                code: trimmedCode,
                name: trimmedName,
                app_type: scalars.app_type,
                org_scope_mode: scalars.org_scope_mode,
                supports_role_management: scalars.supports_role_management,
                redirect_uris: redirectUris.map((v) => v.trim()).filter(Boolean),
                post_logout_redirect_uris: postLogoutUris.map((v) => v.trim()).filter(Boolean),
                ...(scalars.description.trim() ? { description: scalars.description.trim() } : {}),
                ...(trimmedIconUrl ? { icon_url: trimmedIconUrl } : {}),
                ...(scalars.owner_name.trim() ? { owner_name: scalars.owner_name.trim() } : {}),
                ...(trimmedOwnerEmail ? { owner_email: trimmedOwnerEmail } : {}),
                ...(trimmedWebUrl ? { web_url: trimmedWebUrl } : {}),
                ...(scalars.api_base.trim() ? { api_base: scalars.api_base.trim() } : {}),
                ...(scalars.audience.trim() ? { audience: scalars.audience.trim() } : {}),
            };
            return createAdminApp(config.apiBase, accessToken as string, payload);
        },
        onSuccess: (created) => {
            reset();
            toast.success(
                t('console.apps.createForm.createSuccessToast', {
                    name: created.name,
                    code: created.code,
                })
            );
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            // EAA-1819: every mutation on this screen invalidates role-map
            // sync-status too — its health counters (paused/orphan counts)
            // derive from the same registry + role-map data this screen edits.
            void queryClient.invalidateQueries({ queryKey: ['role-map-sync-status'] });
            onCreated(created);
        },
        onError: (error) => {
            if (error instanceof OrgAdminApiError) {
                // EAA-1884: `.details` entries are `{code, message}` objects on
                // the wire — see `normalizeApiErrorDetails`'s own doc comment.
                const details = normalizeApiErrorDetails(error.details);
                if (Object.keys(details).length > 0) {
                    setFieldErrors(details);
                    return;
                }
                setFormError(error.message);
                return;
            }
            setFormError(t('console.apps.createForm.createErrorFallback'));
        },
    });

    function reset() {
        setScalars(EMPTY_SCALARS);
        setRedirectUris(['']);
        setPostLogoutUris(['']);
        setCodeTouched(false);
        setTouched({});
        setFieldErrors({});
        setFormError(null);
    }

    function handleCodeBlur() {
        setCodeTouched(true);
    }

    const codeError =
        codeTouched && trimmedCode && !codeValid
            ? t('console.apps.createForm.codeInvalidError')
            : null;
    const webUrlError =
        touched.web_url && trimmedWebUrl && !webUrlValid
            ? t('console.apps.createForm.webUrlInvalidError')
            : null;
    const iconUrlError =
        touched.icon_url && trimmedIconUrl && !iconUrlValid
            ? t('console.apps.createForm.iconUrlInvalidError')
            : null;
    const ownerEmailError =
        touched.owner_email && trimmedOwnerEmail && !ownerEmailValid
            ? t('console.apps.createForm.emailInvalidError')
            : null;

    function onSubmit(canSubmitNow: boolean, isPending: boolean) {
        if (!canSubmitNow || isPending) return;
        setFormError(null);
        createMutation.mutate();
    }

    return {
        scalars,
        setScalar,
        markTouched,
        redirectUris,
        setRedirectUris,
        postLogoutUris,
        setPostLogoutUris,
        redirectError: touched.redirect_uris ? redirectError : null,
        postLogoutError: touched.post_logout_redirect_uris ? postLogoutError : null,
        codeError,
        webUrlError,
        iconUrlError,
        ownerEmailError,
        fieldErrors,
        formError,
        canSubmit,
        createMutation,
        handleCodeBlur,
        onSubmit,
        reset,
    };
}

export type CreateAppFormVM = ReturnType<typeof useCreateAppForm>;
